import DOMPurify from 'dompurify'

const SAFE_SIGNATURE_URI_PATTERN = /^(?:(?:https?|mailto|tel):)/i
const SAFE_SIGNATURE_URI_OR_IMAGE_DATA_PATTERN = /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpeg|jpg|gif|webp);base64,)/i
const SAFE_IMAGE_URI_PATTERN = /^(?:https?:\/\/|data:image\/(?:png|jpeg|jpg|gif|webp);base64,)/i
const SIGNATURE_IMAGE_ALIGNMENTS = ['left', 'center', 'right']
const SIGNATURE_IMAGE_SIZES = ['small', 'medium', 'large']

const SIGNATURE_SANITIZER_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'div',
        'span',
        'strong',
        'em',
        'u',
        'i',
        'b',
        'a',
        'img',
        'ul',
        'ol',
        'li',
        'blockquote',
        'table',
        'thead',
        'tbody',
        'tfoot',
        'tr',
        'td',
        'th',
        'center',
        'font',
        'small',
        'big',
        'sub',
        'sup',
        'hr',
    ],
    ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'target',
        'rel',
        'style',
        'class',
        'width',
        'height',
        'border',
        'cellpadding',
        'cellspacing',
        'align',
        'color',
        'bgcolor',
        'size',
        'face',
        'valign',
        'data-align',
        'data-size',
    ],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: SAFE_SIGNATURE_URI_OR_IMAGE_DATA_PATTERN,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe', 'style'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur', 'onsubmit'],
}

function unwrapElement(element) {
    if (!element?.parentNode) {
        return
    }

    const parent = element.parentNode

    while (element.firstChild) {
        parent.insertBefore(element.firstChild, element)
    }

    parent.removeChild(element)
}

function signatureImageStyle(align = 'center', size = 'large') {
    const alignments = {
        left: 'display:block;margin:8px auto 8px 0;max-width:100%;height:auto;',
        center: 'display:block;margin:8px auto;max-width:100%;height:auto;',
        right: 'display:block;margin:8px 0 8px auto;max-width:100%;height:auto;',
    }

    const sizes = {
        small: 'width:240px;',
        medium: 'width:480px;',
        large: 'width:100%;',
    }

    return `${alignments[align] || alignments.center}${sizes[size] || sizes.large}`
}

function getSignatureImagePresentation(image) {
    const dataAlign = image.getAttribute('data-align')
    const dataSize = image.getAttribute('data-size')
    const { marginLeft, marginRight, width: styleWidth } = image.style
    const width = styleWidth || image.getAttribute('width') || ''

    if (
        !SIGNATURE_IMAGE_ALIGNMENTS.includes(dataAlign) &&
        !SIGNATURE_IMAGE_SIZES.includes(dataSize) &&
        !width &&
        !marginLeft &&
        !marginRight
    ) {
        return null
    }

    let align = SIGNATURE_IMAGE_ALIGNMENTS.includes(dataAlign) ? dataAlign : 'center'

    if (!SIGNATURE_IMAGE_ALIGNMENTS.includes(dataAlign)) {
        if (marginLeft === 'auto' && marginRight === 'auto') {
            align = 'center'
        } else if (marginRight === 'auto') {
            align = 'left'
        } else if (marginLeft === 'auto') {
            align = 'right'
        }
    }

    let size = SIGNATURE_IMAGE_SIZES.includes(dataSize) ? dataSize : 'large'

    if (!SIGNATURE_IMAGE_SIZES.includes(dataSize)) {
        const pixelWidth = Number.parseFloat(width)

        if (/100%/.test(width)) {
            size = 'large'
        } else if (Number.isFinite(pixelWidth) && pixelWidth <= 240) {
            size = 'small'
        } else if (Number.isFinite(pixelWidth) && pixelWidth <= 480) {
            size = 'medium'
        }
    }

    return { align, size }
}

export function normalizeSignatureImagePresentation(html = '') {
    if (!html || typeof DOMParser === 'undefined') {
        return html || ''
    }

    const parser = new DOMParser()
    const document = parser.parseFromString(String(html), 'text/html')

    document.querySelectorAll('img').forEach((image) => {
        const presentation = getSignatureImagePresentation(image)

        if (presentation) {
            image.setAttribute('data-align', presentation.align)
            image.setAttribute('data-size', presentation.size)
            image.setAttribute('style', signatureImageStyle(presentation.align, presentation.size))
        }
    })

    return document.body.innerHTML
}

function normalizeSignatureDocument(document) {
    let strippedImageCount = 0

    document.querySelectorAll('a[href]').forEach((link) => {
        const href = (link.getAttribute('href') || '').trim()

        if (!SAFE_SIGNATURE_URI_PATTERN.test(href)) {
            unwrapElement(link)
            return
        }

        link.setAttribute('href', href)
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
    })

    document.querySelectorAll('img').forEach((image) => {
        const src = (image.getAttribute('src') || '').trim()

        if (!SAFE_IMAGE_URI_PATTERN.test(src)) {
            strippedImageCount += 1
            image.remove()
            return
        }

        image.setAttribute('src', src)
        image.removeAttribute('srcset')
        image.removeAttribute('sizes')
        image.removeAttribute('loading')
        image.removeAttribute('data-content-id')
    })

    return strippedImageCount
}

export function sanitizeEmailSignature(html = '') {
    if (!html) {
        return ''
    }

    return DOMPurify.sanitize(normalizeSignatureImagePresentation(html), SIGNATURE_SANITIZER_CONFIG)
}

export function cleanEmailSignatureHtml(html = '') {
    const fallbackHtml = sanitizeEmailSignature(html)

    if (!html || typeof DOMParser === 'undefined') {
        return {
            html: fallbackHtml,
            strippedImageCount: 0,
        }
    }

    const parser = new DOMParser()
    const document = parser.parseFromString(String(html), 'text/html')
    const strippedImageCount = normalizeSignatureDocument(document)

    return {
        html: sanitizeEmailSignature(document.body.innerHTML),
        strippedImageCount,
    }
}

export function isBlankSignatureHtml(html) {
    if (!html) {
        return true
    }

    if (typeof DOMParser === 'undefined') {
        const text = String(html)
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .trim()

        return !text && !/<img/i.test(String(html))
    }

    const parser = new DOMParser()
    const document = parser.parseFromString(String(html), 'text/html')
    const text = document.body.textContent.replace(/\u00a0/g, ' ').trim()

    return !text && !document.body.querySelector('img')
}

export function htmlContainsSignatureToken(html = '') {
    return /{{signature}}/i.test(String(html || ''))
}

export function applySignatureToBody(body = '', signature = '', shouldAppend = false) {
    const safeSignature = sanitizeEmailSignature(signature)
    const bodyHtml = String(body || '')

    if (!safeSignature || isBlankSignatureHtml(safeSignature) || !shouldAppend) {
        return bodyHtml.replace(/{{signature}}/gi, '')
    }

    if (htmlContainsSignatureToken(bodyHtml)) {
        return bodyHtml.replace(/{{signature}}/gi, safeSignature)
    }

    if (!shouldAppend || bodyHtml.includes(safeSignature)) {
        return bodyHtml
    }

    return `${bodyHtml}<div><br></div>${safeSignature}`
}
